import { FC, Fragment, useState } from 'react';

import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Element } from 'react-scroll';
import { LearningTypeId } from 'src/types/product';

import CourseAcademicFooterBanner from './courseAcademicBanner/footer';
import CourseAcademicHeaderBanner from './courseAcademicBanner/header';
import CourseDiscussionBoard from './courseDiscussionBoard';

import ScrollTop from '@components/atoms/scrollTop';
import Container from '@components/store/container';
import CourseCurriculum from '@components/store/product/courseCurriculum';
import CourseReview from '@components/store/product/courseReview';
import CourseSuggestion from '@components/store/product/courseSuggestion';
import ProductCriteriaCertificate from '@components/store/product/productCriteriaCertificate';
import ProductCriteriaCertificateBundle from '@components/store/product/productCriteriaCertificateBundle';
import ProductInstructor from '@components/store/product/productInstructor';
import ProductNavigationMenu, { ProductNavigationMenuItem } from '@components/store/product/productNavigationMenu';
import ProductPaymentStep from '@components/store/product/productPaymentStep';
import CourseDescription from '@components/templates/courseDetail/courseDescription';
import CourseRegularFooterBanner from '@components/templates/courseDetail/courseRegularBanner/footer';
import CourseRegularHeaderBanner from '@components/templates/courseDetail/courseRegularBanner/header';
import { MIN_COURSE_REVIEW } from '@hooks/course/courseReview/useCourseReview';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { ICategory } from '@interfaces/category';
import { CourseEntity } from '@redux/course/type/courseDetailType';
import { AllVersion } from 'src/types/learningPath';

export const COURSE_REGULAR_PRESET_MENUS = {
  DESCRIPTION: 'description',
  CURRICULUM: 'curriculum',
  INSTRUCTOR: 'instructor',
  CERTIFICATE: 'certificate',
  CERTIFICATE_BUNDLE: 'certificate_bundle',
  REVIEW: 'review',
  DISCUSSION: 'discussion',
  PAYMENT_STEP: 'payment-step',
};

type CourseDetailSectionProps = {
  courseDetailSeo: CourseEntity;
  learningPath?: AllVersion;
  learningPathCategory?: ICategory;
  courseCount?: string;
};

const CourseDetailSection: FC<CourseDetailSectionProps> = ({
  courseDetailSeo,
  learningPath,
  learningPathCategory,
  courseCount,
}) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const { hasCertificate, review, canDiscuss, hasCertificateBundle } = useCourseDetail();
  const isDisplayCourseReview = (review?.totalReview ?? 0) >= MIN_COURSE_REVIEW;
  const isDisplayCourseDiscussion = !!canDiscuss;
  const productLearningTypeId = courseDetailSeo?.productLearningTypeId;

  const isRegular =
    productLearningTypeId === LearningTypeId.REG ||
    productLearningTypeId === LearningTypeId.EXL ||
    productLearningTypeId === LearningTypeId.MCL;

  const isAcademic = productLearningTypeId === LearningTypeId.ACM;

  const [headerHeight, setHeaderHeight] = useState<number>(0);

  const menus: ProductNavigationMenuItem[] = [
    {
      label: t('course_detail.navigation_menu.course_detail'),
      value: COURSE_REGULAR_PRESET_MENUS.DESCRIPTION,
    },
    {
      label: t('course_detail.navigation_menu.course_curriculum'),
      value: COURSE_REGULAR_PRESET_MENUS.CURRICULUM,
    },
    {
      label: t('course_detail.navigation_menu.course_instructor'),
      value: COURSE_REGULAR_PRESET_MENUS.INSTRUCTOR,
    },
    ...(hasCertificate
      ? [
          {
            label: t('course_detail.navigation_menu.course_certificate'),
            value: COURSE_REGULAR_PRESET_MENUS.CERTIFICATE,
          },
        ]
      : []),
    ...(hasCertificateBundle
      ? [
          {
            label: t('course_detail.navigation_menu.course_learning_path'),
            value: COURSE_REGULAR_PRESET_MENUS.CERTIFICATE_BUNDLE,
            sx: { textTransform: 'unset' as any },
          },
        ]
      : []),
    ...(isDisplayCourseReview
      ? [
          {
            label: t('course_detail.navigation_menu.course_review'),
            value: COURSE_REGULAR_PRESET_MENUS.REVIEW,
          },
        ]
      : []),
    {
      label: t('course_detail.navigation_menu.course_discussion'),
      value: COURSE_REGULAR_PRESET_MENUS.DISCUSSION,
    },
    {
      label: t('course_detail.navigation_menu.course_payment_step'),
      value: COURSE_REGULAR_PRESET_MENUS.PAYMENT_STEP,
    },
  ];

  const customScrollOffset = {
    [COURSE_REGULAR_PRESET_MENUS.CURRICULUM]: -150,
    [COURSE_REGULAR_PRESET_MENUS.INSTRUCTOR]: -150,
  };

  return (
    <Fragment>
      {isRegular && (
        <CourseRegularHeaderBanner
          onHeaderSizeChange={(height: number) => setHeaderHeight(height)}
          learningPath={learningPath}
          learningPathCategory={learningPathCategory}
          courseCount={courseCount}
          courseDetailSeo={courseDetailSeo}
        />
      )}
      {isAcademic && (
        <CourseAcademicHeaderBanner
          onHeaderSizeChange={(height: number) => setHeaderHeight(height)}
          learningPath={learningPath}
          learningPathCategory={learningPathCategory}
          courseCount={courseCount}
          courseDetailSeo={courseDetailSeo}
        />
      )}

      <ProductNavigationMenu
        defaultMenu={COURSE_REGULAR_PRESET_MENUS.DESCRIPTION}
        menus={menus}
        customScrollOffset={customScrollOffset}
      />
      <Element name={COURSE_REGULAR_PRESET_MENUS.DESCRIPTION}>
        <Container
          containerSx={{
            display: 'grid',
            gridTemplateColumns: { lg: '60% auto' },
            gridTemplateRows: { lg: '40px 1fr' },
            gap: 2,
            pt: { xs: 3, md: 5, lg: 4.5 },
            pb: 0,
            px: { xs: 3, lg: 0 },
          }}
        >
          <CourseDescription courseDetailSeo={courseDetailSeo} />
        </Container>
      </Element>

      <Box pt={{ xs: 5, md: 3, lg: 6 }} pb={{ xs: 6, md: 8, lg: 6 }}>
        <Container
          containerSx={{
            px: { xs: 3, lg: 0 },
            display: 'grid',
            gridTemplateColumns: { lg: '1fr 40%' },
            gap: { xs: 5, lg: 2 },
          }}
        >
          <CourseCurriculum
            scrollElementName={COURSE_REGULAR_PRESET_MENUS.CURRICULUM}
            courseDetailSeo={courseDetailSeo}
          />
          <ProductInstructor
            scrollElementName={COURSE_REGULAR_PRESET_MENUS.INSTRUCTOR}
            instructors={courseDetailSeo?.instructors}
          />
        </Container>
      </Box>
      {hasCertificate ? (
        <ProductCriteriaCertificate
          scrollElementName={COURSE_REGULAR_PRESET_MENUS.CERTIFICATE}
          bgColor={theme.palette.grey[100]}
        />
      ) : null}
      {hasCertificateBundle ? (
        <ProductCriteriaCertificateBundle
          scrollElementName={COURSE_REGULAR_PRESET_MENUS.CERTIFICATE_BUNDLE}
          courseId={courseDetailSeo.id}
        />
      ) : null}
      <CourseReview
        scrollElementName={COURSE_REGULAR_PRESET_MENUS.REVIEW}
        isDisplayCourseReview={isDisplayCourseReview}
      />
      <CourseDiscussionBoard
        scrollElementName={COURSE_REGULAR_PRESET_MENUS.DISCUSSION}
        isDisplayCourseDiscussion={isDisplayCourseDiscussion}
      />
      <ProductPaymentStep scrollElementName={COURSE_REGULAR_PRESET_MENUS.PAYMENT_STEP} />
      <CourseSuggestion courseIdName={courseDetailSeo.idName} />
      <ScrollTop bottom={170} scrollDuration={0} />

      {isRegular && <CourseRegularFooterBanner headerHeight={headerHeight} courseDetailSeo={courseDetailSeo} />}
      {isAcademic && <CourseAcademicFooterBanner headerHeight={headerHeight} courseDetailSeo={courseDetailSeo} />}
    </Fragment>
  );
};

export default CourseDetailSection;
